// pdf-content.builder.ts
import htmlToPdfmake from 'html-to-pdfmake';

/**
 * Entfernt aus dem HTML-String alle Inline-Styles außer "font-weight".
 */
export function filterStyles(html: string): string {
  return html.replace(/style="([^"]*)"/gi, (match, styleContent) => {
    const allowedStyles = styleContent
      .split(';')
      .map((rule:any) => rule.trim())
      .filter((rule:any) => rule.toLowerCase().startsWith('font-weight'));
    return allowedStyles.length > 0 ? `style="${allowedStyles.join('; ')}"` : '';
  });
}


export function createSectionContent(section: any, currentLanguage: string, statisticTable: any[], charts: any[], sidebarActive: boolean, comment?: string, product?: any): any[] {
  const sectionContent: any[] = [];
  const sectionTranslation = section.translations[currentLanguage];
  if (!sectionTranslation) {
    return sectionContent;
  }


  // Prüfe, ob der Titel "disclaimer" oder "haftungsausschluss" (case-insensitive) ist
  const titleLower = sectionTranslation.title ? sectionTranslation.title.toLowerCase() : '';
  const isDisclaimer = titleLower === 'disclaimer' || titleLower === 'haftungsausschluss';

  // Prüfe, ob die Sektion den {{comment}}-Platzhalter enthält
  let hasCommentPlaceholder = false;

  // Prüfe den Section-Titel
  if (sectionTranslation.title && sectionTranslation.title.includes('{{comment}}')) {
    hasCommentPlaceholder = true;
  }

  // Falls nicht im Titel, suche in den Editor-Blöcken
  if (!hasCommentPlaceholder && section.editor_blocks) {
    for (const block of section.editor_blocks) {
      const blockTranslation = block.translations[currentLanguage];
      if (blockTranslation && blockTranslation.content_rendered) {
        for (const contentBlock of blockTranslation.content_rendered) {
          if (
            contentBlock.data &&
            typeof contentBlock.data.text === 'string' &&
            contentBlock.data.text.includes('{{comment}}')
          ) {
            hasCommentPlaceholder = true;
            break;
          }
        }
      }
      if (hasCommentPlaceholder) break;
    }
  }

  // Wenn die Sektion für einen Kommentar vorgesehen ist, aber kein Kommentar übergeben wurde,
  // wird die gesamte Sektion übersprungen.
  if (hasCommentPlaceholder && (!comment || comment.trim() === '')) {
    return [];
  }

  // Helferfunktion zur Platzhalter-Ersetzung:
  const replaceComment = (text: string) => comment ? text.replace(/{{comment}}/g, comment) : text;

  // Section Title
  if (sectionTranslation.title) {
    const title = replaceComment(sectionTranslation.title);
    const titleElement: any = { text: title, style: 'header' };
    if (isDisclaimer) {
      titleElement.fontSize = 6;
    }
    sectionContent.push(titleElement);
  }

  const editorBlocks = section.editor_blocks;
  editorBlocks.forEach((block: any) => {
    const blockTranslation = block.translations[currentLanguage];
    if (!blockTranslation) {
      return;
    }
    if (blockTranslation.content_rendered) {
      blockTranslation.content_rendered.forEach((contentBlock: any) => {
        if (contentBlock.type === 'paragraph') {
          const rawText = replaceComment(contentBlock.data.text);
          const cleanedHeader = filterStyles(rawText);
          const pdfContent = htmlToPdfmake(cleanedHeader);

          const element: any = { text: pdfContent, style: 'paragraph' };
          if (isDisclaimer) {
            element.fontSize = 6;
          }
          sectionContent.push(element);
        } else if (contentBlock.type === 'header') {
          const pdfContent = htmlToPdfmake(contentBlock.data.text);
          const element: any = { text: pdfContent, style: 'headerSmall' };
          if (isDisclaimer) {
            element.fontSize = 6;
          }
          sectionContent.push(element);
        } else if (contentBlock.type === 'table') {
          console.log('content block table', contentBlock.data);
          sectionContent.push(createTable(contentBlock.data));
        } else if (contentBlock.type === 'list') {
          sectionContent.push(createList(contentBlock.data));
        }
      });
    }
  });

  const statistic = statisticTable.find(stat => stat.id === section.id);
  if (statistic) {
    sectionContent.push(createStatisticTable(statistic.data, product?.phases[0]));
  }

  const chart = charts.find(chart => chart.id === section.id);
  if (chart && chart.image) {
    const imageWidth = getImageWidth(section.width);
    if(sidebarActive) {
      sectionContent.push({ svg: chart.image, width: imageWidth - 110 });
    } else {
      sectionContent.push({ svg: chart.image, width: imageWidth });
    }

  }

  return sectionContent;
}

export function createStatisticTableBackup(data: any): any {
  const header = [''].concat(data[0].product.map((product: any) => product.name));
  const body = [header];
  data.forEach((item: any) => {
    const row = [item.statistic].concat(item.product.map((product: any) => product.value));
    body.push(row);
  });
  return {
    table: {
      headerRows: 1,
      widths: Array(header.length).fill('*'),
      body: body
    },
    layout: {
      hLineWidth: () => 0.25,
      hLineColor: () => '#1F273C',
      vLineWidth: () => 0
    },
    margin: [0, 0, 0, 5]
  };
}

export function createTable(data: any): any {
  // Überarbeiten der Zellen: Schriftgröße festlegen, und Header-Zeilen anpassen
  const body = data.content.map((row: any, rowIndex: number) => {
    return row.map((cell: any) => {
      let cellContent: any;
      if (typeof cell === 'string') {
        // Zuerst unerwünschte Styles filtern
        const cleanedCell = filterStyles(cell);
        // Optional: HTML-Konvertierung vornehmen, wenn die Zelle HTML enthält
        cellContent = htmlToPdfmake(cleanedCell);
      } else {
        cellContent = cell;
      }
      // Falls das Ergebnis von htmlToPdfmake ein Array ist, musst du eventuell anpassen,
      // je nachdem wie du es in der Tabelle darstellen möchtest.
      let cellObj: any;
      if (typeof cellContent === 'string') {
        cellObj = { text: cellContent, fontSize: 6.5 };
      } else if (Array.isArray(cellContent)) {
        // Wenn es ein Array ist, benutze 'stack'
        cellObj = { stack: cellContent, fontSize: 6.5 };
      } else {
        cellObj = { ...cellContent, fontSize: 6.5 };
      }

      // Falls es sich um den Header handelt, Textfarbe auf Weiß setzen.
      if (data.withHeadings && rowIndex === 0) {
        cellObj.color = 'white';
      }
      return cellObj;
    });
  });

  return {
    table: {
      headerRows: data.withHeadings ? 1 : 0,
      widths: Array(data.content[0].length).fill('*'),
      body: body,
    },
    layout: {
      // Layout-Funktion für den Hintergrund (fillColor)
      fillColor: (rowIndex: number, node: any, columnIndex: number) => {
        if (data.withHeadings) {
          // Zeile 0 = Header: Hintergrund #1F273C
          if (rowIndex === 0) {
            return '#1F273C';
          } else if (rowIndex % 2 === 1) {
            // Alle ungeraden Zeilen (bei Vorhandensein eines Headers: Zeile 1, 3, …)
            return '#F4F2F1';
          }
        } else {
          // Falls kein Header: 1-basiert ungerade Zeilen einfärben
          if ((rowIndex + 1) % 2 === 1) {
            return '#F4F2F1';
          }
        }
        return null;
      },
      hLineWidth: () => 0.25,
      hLineColor: () => '#F4F2F1',
      vLineWidth: () => 0,
    },
    margin: [0, 0, 0, 10],
  };
}






export function createList(data: any): any {
  const items = data.items.map((item: string) => ({ text: item, style: 'listItem' }));
  return {
    ul: items,
    margin: [0, 0, 0, 10],
    style: data.style === 'ordered' ? 'orderedList' : 'unorderedList'
  };
}

export function createColumn(section: any, sectionContent: any[]): any {
  const widthPercentage = getWidthPercentage(section.width);
  return {
    width: widthPercentage,
    stack: sectionContent,
    unbreakable: true
  };
}

export function getSectionWidth(width: number): number {
  switch (width) {
    case 1:
      return 33.33;
    case 2:
      return 66.66;
    case 3:
      return 100;
    default:
      return 100;
  }
}

export function getWidthPercentage(width: number): string {
  switch (width) {
    case 1:
      return '33.33%';
    case 2:
      return '66.66%';
    case 3:
      return '100%';
    default:
      return '100%';
  }
}

export function getImageWidth(width: number): number {
  switch (width) {
    case 1:
      return 162;
    case 2:
      return 324;
    case 3:
      return 510;
    default:
      return 510;
  }
}

export function hasColoredPdfCell(row: string[], phases: any): boolean {
  for (let i = 1; i < row.length - 1; i++) {
    if (shouldColorPdfCell(row, i, phases)) {
      return true;
    }
  }
  return false;
}


// Hilfsfunktion zum Überprüfen, ob eine Zelle eingefärbt werden soll.
export function shouldColorPdfCell(
  row: string[],
  colIndex: number,
  phases: any
): boolean {
  // Spalte 0 (Jahr) und letzte Spalte (Ann.) nicht einfärben
  if (colIndex === 0 || colIndex === row.length - 1) {
    return false;
  }
  // Lese das Jahr aus der ersten Spalte
  const year = parseInt(row[0], 10);
  // Spalte 1 entspricht Januar, 2 Februar, usw.
  const month = colIndex;
  const cellDate = new Date(year, month - 1, 1);
  const phaseStart = new Date(phases.start_date);
  const phaseEnd = new Date(phases.end_date);
  return cellDate >= phaseStart && cellDate <= phaseEnd;
}

export function createStatisticTable(
  data: any,
  phases?: any
): any {
  console.log('createStatisticTablePdf', data);
  const body = data.content.map((row: any, rowIndex: number) => {
    return row.map((cell: any, colIndex: number) => {
      let cellContent: any;
      if (typeof cell === 'string') {
        const cleanedCell = filterStyles(cell);
        cellContent = htmlToPdfmake(cleanedCell);
      } else {
        cellContent = cell;
      }
      let cellObj: any;
      if (typeof cellContent === 'string') {
        cellObj = { text: cellContent, fontSize: 6.5 };
      } else if (Array.isArray(cellContent)) {
        cellObj = { stack: cellContent, fontSize: 6.5 };
      } else {
        cellObj = { ...cellContent, fontSize: 6.5 };
      }
      // Header-Zeile: immer weiß
      if (data.withHeadings && rowIndex === 0) {
        cellObj.color = 'white';
      }

      // Für alle Zeilen unterhalb der Header
      if (phases && rowIndex > 0) {
        // Für die letzte Spalte prüfen wir, ob in irgendeiner Zelle (außer erster/letzter) eingefärbt werden soll.
        if (colIndex === row.length - 1) {
          if (hasColoredPdfCell(row, phases)) {
            // Falls bereits ein Text vorhanden ist, hänge ein Sternchen an.
            if (cellObj.text) {
              cellObj.text = cellObj.text + '*';
            } else if (cellObj.stack) {
              cellObj.stack.push({ text: '*', color: '#ADA29A', fontSize: 6.5 });
            }
            cellObj.color = '#ADA29A';
          }
        }
        // Für alle anderen (außer der ersten Spalte) anwenden:
        else if (colIndex !== 0) {
          if (shouldColorPdfCell(row, colIndex, phases)) {
            if (cellObj.text) {
              cellObj.text = cellObj.text + '*';
            } else if (cellObj.stack) {
              cellObj.stack.push({ text: '*', color: '#ADA29A', fontSize: 6.5 });
            }
            cellObj.color = '#ADA29A';
          }
        }
      }
      return cellObj;
    });
  });

  return {
    table: {
      headerRows: data.withHeadings ? 1 : 0,
      widths: Array(data.content[0].length).fill('*'),
      body: body,
    },
    layout: {
      fillColor: (rowIndex: number, node: any, columnIndex: number) => {
        if (data.withHeadings) {
          if (rowIndex === 0) {
            return '#1F273C';
          } else if (rowIndex % 2 === 1) {
            return '#F4F2F1';
          }
        } else {
          if ((rowIndex + 1) % 2 === 1) {
            return '#F4F2F1';
          }
        }
        return null;
      },
      hLineWidth: () => 0.25,
      hLineColor: () => '#F4F2F1',
      vLineWidth: () => 0,
    },
    margin: [0, 0, 0, 10],
  };
}


